import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { GET } from 'services/api';
import { UpdateEnvioSol } from 'services/pre-proposal';
import { Icon, Load, StatusContent, Title } from 'ui/styled';
import { marcadorDiferente, normalizeResponse, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingPreAnalise() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [rowFetchingId, setRowFetchingId] = useState(0);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);
  const opcao = 'preanalise';

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const handleTagOnClick = useCallback(
    async (row) => {
      if (!!rowFetchingId) return;
      try {
        setRowFetchingId(row?.id);

        if (row?.status === 'Avançar' && user?.administrador) {
          const payload = { data: { status: 'aprovado' } };

          await UpdateEnvioSol(payload, row?.id);

          toast.success('Proposta confirmada com sucesso.', {
            autoClose: 2000,
            onClose: () => {
              console.log('terminou');

              setRowFetchingId(0);
              history.go(0);
            },
          });

          return;
        }
        if (!user?.administrador) {
          throw new Error('Sem permissão');
        } else {
          navigate(`financingtablepreanalise/${row?.id}/preanalise`);
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message);
        setRowFetchingId(0);
      }
    },
    [history, navigate, rowFetchingId, user?.administrador]
  );

  // Para a pagina nao abrir caso nao seja com esses dois nomes.
  const init = useCallback(async () => {
    setLoading(true);
    try {
      const populate = 'distribuidor,integrador,banco,uf,bancos';
      // Ajuste o valor exato se na sua API vier diferente (ex: "em_analise", "Em análise", etc.)
      const url = `/pre-proposals?populate=${populate}&filters[status][$eq]=pendente`;
      const result = await GET(url, true);
      const data = normalizeResponse(result);
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
      const next = sorted.map((item) => ({
        ...item,
        accepted: (item.bancos || []).find((f) => f.aceito),
      }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar pré-propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          let marca;
          let distribuidor = row?.idDistribuidor;
          // Busca no array de bancos o id do banco que for igual ao id que escolher
          let banco = row?.bancos.find((banco) => banco.banco.id === 70)?.banco
            .id;
          // Id distribuidor 1152 é da fotus e banco id 70 é da omni do srv de producao.
          if (distribuidor === 1152 && banco === 70) {
            if (row.marca) {
              marca = marcadorDiferente(row?.id, '@');
              return marca;
            } else {
              marca = row?.id;
              return marca;
            }
          } else {
            marca = row?.id;
            return marca;
          }
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      {
        title: 'SLA',
        aling: 'center',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value', renderCell: ({ row }) => row?.value },
      { title: 'Integrador', sortable: true, ref: 'nome_integrador' },
      { title: 'Vendedor', sortable: true, ref: 'vendedor' },
      {
        title: 'Telefone Vendedor',
        renderCell: ({ row }) => {
          if (!row?.telefone) return '-';
          const numeroLimpo = row.telefone.replace(/\D/g, '');
          const linkWhats = `https://wa.me/55${numeroLimpo}`;
          return (
            <a
              href={linkWhats}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {row.telefone}
            </a>
          );
        },
      },
      {
        title: 'TAG',
        ref: 'status',
        renderCell: ({ row }) => (
          // transformado a tag em um botao clicavel, depois de pelo menos um pre-aprovado, vai poder estar clicando em Avançar.
          <StatusContent
            color={selectColors(row?.status)}
            style={{ cursor: 'pointer' }}
            onClick={() => handleTagOnClick(row)}
            center={rowFetchingId === row?.id && true}
          >
            {rowFetchingId === row?.id ? (
              <Load white />
            ) : (user?.administrador === false) &
              (row?.status === 'Não Consultado') ? (
              'Em Análise'
            ) : (
              row?.status
            )}
          </StatusContent>
        ),
      },
      // Inserir coluna "Espelho da Proposta" e "Seta" condicionalmente
      ...(user?.administrador
        ? [
            {
              title: 'Espelho da Proposta',
              ref: 'espelho',
              renderCell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={() => navigate(`financingproposal/${row?.id}`)}
                    color={'blue'}
                    nospace
                    mbutton
                    small
                  >
                    Visualizar
                  </Button>
                </div>
              ),
            },
            {
              title: '',
              renderCell: ({ row }) => (
                <Icon
                  icon="arrow-right"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(`financingtablepreanalise/${row?.id}/${opcao}`)
                  }
                />
              ),
            },
          ]
        : []),
    ];
    return baseColumns;
  }, [user, navigate, handleTagOnClick, rowFetchingId]);
  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.id,
      date: a?.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.createdAt),
      distributor: normalizeName(a?.distribuidor?.trade_name) || '-',
      customer: normalizeName(a?.nome_completo) || '-',
      value: parseCurrency(a?.valor_financiamento),
      status: a?.bancos?.find((banco) => banco.status === 'aprovado')
        ? 'Avançar'
        : 'Não Consultado',
      bancos: a?.bancos,
      telefone: a?.telefone_vendedor || null,
      vendedor: normalizeName(a?.nome_vendedor) || '-',
      marca: a?.integrador?.marca_distribuidor_banco,
      idDistribuidor: a?.distribuidor?.id,
      nome_integrador: normalizeName(a?.nome_integrador) || '-',
      cnpj_integrador: a?.cnpj_integrador,
      cnpj_integrador_clean: a?.cnpj_integrador.replace(/\D/g, ''),
      cpf_cnpj: a?.cpf || a?.cnpj,
      cpf_cnpj_clean: a?.cpf
        ? a?.cpf.replace(/\D/g, '')
        : a?.cnpj.replace(/\D/g, ''),
    }));
  }, [registers]);

  useEffect(() => init(), [tick, init]);

  return (
    <ContainerAuthenticated>
      <Title>Pré-Financiamento</Title>
      <TableWrapper title="Pré-Análise" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
        {user?.administrador || user?.distribuidor?.id === 1152
          ? '@ marca para identificar integrador cadastrado na omni.'
          : null}
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
